import type { CalendarVisibleMonths } from 'components/Common/Calendar/calendarFocusMonth'
import { toCalendarMonthKey } from 'components/Common/Calendar/calendarFocusMonth'
import type { SearchCalendarEvent } from 'constants/searchCalendarEvents'
import { getDateFromMonthKey, getLastDayOfMonth } from 'lib/datetime/dateUtils'

/**
 * How many months either side of a visible month still count as "near" an
 * event for experiment exposure. Someone looking at August when the event is in
 * September is part of the measurable population even though nothing is painted
 * on their calendar.
 */
const EXPOSURE_MONTH_TOLERANCE = 1

const MS_PER_DAY = 24 * 60 * 60 * 1000

function padDay(day: number): string {
  return `${day}`.padStart(2, '0')
}

function monthKeyToInclusiveIsoRange(monthKey: string): { startDate: string, endDate: string } {
  const monthStart = getDateFromMonthKey(monthKey)
  const lastDay = getLastDayOfMonth(monthStart)
  return {
    startDate: `${monthKey}-01`,
    endDate: `${monthKey}-${padDay(lastDay)}`,
  }
}

function isoRangesOverlap(
  startDate: string,
  endDate: string,
  otherStartDate: string,
  otherEndDate: string,
): boolean {
  return startDate <= otherEndDate && otherStartDate <= endDate
}

function toMonthKeys(visibleMonths?: CalendarVisibleMonths): Array<string> {
  return [visibleMonths?.left, visibleMonths?.right].filter(
    (monthKey): monthKey is string => !!monthKey,
  )
}

/** Offsets a `YYYY-MM` key by whole months, rolling the year over as needed. */
export function offsetMonthKey(monthKey: string, monthOffset: number): string {
  const monthStart = getDateFromMonthKey(monthKey)
  return toCalendarMonthKey(new Date(monthStart.getFullYear(), monthStart.getMonth() + monthOffset))
}

function eventsOverlappingMonthKeys(
  events: ReadonlyArray<SearchCalendarEvent>,
  monthKeys: ReadonlyArray<string>,
): Array<SearchCalendarEvent> {
  if (!monthKeys.length) return []

  return events.filter(event => monthKeys.some(monthKey => {
    const monthRange = monthKeyToInclusiveIsoRange(monthKey)
    return isoRangesOverlap(event.startDate, event.endDate, monthRange.startDate, monthRange.endDate)
  }))
}

export function eventsForPlaceIds(
  events: ReadonlyArray<SearchCalendarEvent>,
  placeIds: ReadonlyArray<string>,
): Array<SearchCalendarEvent> {
  if (!placeIds.length) return []

  const placeIdSet = new Set(placeIds)
  return events.filter(event => event.searchPlaceIds.some(placeId => placeIdSet.has(placeId)))
}

/** Events whose dates fall inside a visible month — i.e. what gets painted. */
export function eventsIntersectingMonths(
  events: ReadonlyArray<SearchCalendarEvent>,
  visibleMonths?: CalendarVisibleMonths,
): Array<SearchCalendarEvent> {
  return eventsOverlappingMonthKeys(events, toMonthKeys(visibleMonths))
}

/**
 * Events within {@link EXPOSURE_MONTH_TOLERANCE} months of a visible month.
 *
 * Widens experiment exposure only — never rendering. A user one month either
 * side of an event month is comparable to one looking straight at it, so both
 * belong in the experiment population.
 */
export function eventsNearMonths(
  events: ReadonlyArray<SearchCalendarEvent>,
  visibleMonths?: CalendarVisibleMonths,
): Array<SearchCalendarEvent> {
  const nearMonthKeys = new Set<string>()
  for (const monthKey of toMonthKeys(visibleMonths)) {
    for (let offset = -EXPOSURE_MONTH_TOLERANCE; offset <= EXPOSURE_MONTH_TOLERANCE; offset++) {
      nearMonthKeys.add(offsetMonthKey(monthKey, offset))
    }
  }

  return eventsOverlappingMonthKeys(events, [...nearMonthKeys])
}

export function shouldActivateSearchCalendarEventOverlay(
  events: ReadonlyArray<SearchCalendarEvent>,
  placeIds: ReadonlyArray<string>,
  visibleMonths?: CalendarVisibleMonths,
): boolean {
  return eventsNearMonths(eventsForPlaceIds(events, placeIds), visibleMonths).length > 0
}

/**
 * The one event allowed per visible 2-month block.
 *
 * The desktop picker's viewport *is* the block, so capping the events it
 * intersects caps the block. Earliest start wins, with the id as a tie-break so
 * the choice never depends on the order events happen to be declared in.
 */
export function selectPrimaryEvent(
  events: ReadonlyArray<SearchCalendarEvent>,
): SearchCalendarEvent | undefined {
  return events.toSorted((a, b) => (
    a.startDate.localeCompare(b.startDate) || a.id.localeCompare(b.id)
  ))[0]
}

/** Inclusive count of an event's days that land inside a `YYYY-MM` month. */
export function countHighlightedDatesInMonth(
  event: Pick<SearchCalendarEvent, 'startDate' | 'endDate'>,
  monthKey: string,
): number {
  const monthRange = monthKeyToInclusiveIsoRange(monthKey)
  if (!isoRangesOverlap(event.startDate, event.endDate, monthRange.startDate, monthRange.endDate)) {
    return 0
  }

  const firstIso = event.startDate > monthRange.startDate ? event.startDate : monthRange.startDate
  const lastIso = event.endDate < monthRange.endDate ? event.endDate : monthRange.endDate
  return Math.round((Date.parse(lastIso) - Date.parse(firstIso)) / MS_PER_DAY) + 1
}

/**
 * Events a stay actually spends a night during.
 *
 * A stay occupies the nights `[checkIn, checkOut)` — the room is vacated on the
 * morning of check-out — so the check-out day is exclusive. Treating it as
 * inclusive counts a stay that checks out on an event's first day as attending
 * that event despite spending no night during it, which inflates the conversion
 * numerator. The check-in side stays inclusive because the event's end date is
 * itself a night that can be spent.
 */
/**
 * The one event to preview for a single month, or undefined.
 *
 * Used for the chevron previews, which hint at the month a page would newly
 * reveal, so the same single-event-per-block cap applies.
 */
export function eventForMonthKey(
  events: ReadonlyArray<SearchCalendarEvent>,
  monthKey?: string,
): SearchCalendarEvent | undefined {
  if (!monthKey) return undefined

  return selectPrimaryEvent(eventsOverlappingMonthKeys(events, [monthKey]))
}

export function eventsOverlappingStay(
  events: ReadonlyArray<SearchCalendarEvent>,
  checkInIso: string,
  checkOutIso: string,
): Array<SearchCalendarEvent> {
  if (!checkInIso || !checkOutIso) return []

  return events.filter(event => event.startDate < checkOutIso && checkInIso <= event.endDate)
}
